import React from 'react';
import { TextField } from '@mui/material';
import AppFilterGrid, { AppFilterResetButton } from '../layout/AppFilterGrid';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters, resetFilter } from '../../reduxStore/documentVenteSlice';
import ContratAutoC from '../AutoComplete/ContratAutoC';
import SelectDepot from '../Selects/SelectDepot';
import SelectDepartement from '../Selects/SelectDepartement';
import { Departement, Document_vente, GeneriqueData, SysTypeDocument } from '../../models/models';
import NumeroDocAutoC from '../AutoComplete/NumeroDocAutoC';
import SelectSysTypeDocument from '../Selects/SelectSysTypeDocument';
import SelectWorkflowStatut from '../Selects/SelectWorkflowStatut';
import { TypeTiers } from '../../constants/enums';
import { WorkflowStatut } from '../../models/models';
import ClientAutoC from '../AutoComplete/ClientAutoC';
import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';

const DocumentVenteFilter = () => {
  const dispatch = useDispatch();
  const [resetKey, setResetKey] = React.useState(0);
  const { filters } = useSelector((state: any) => state.DocumentVenteSlice);
  const { isAllMode } = useSelector((state: any) => state.AppSlice);

  const [currentTiersType, setCurrentTiersType] = React.useState<TypeTiers | null>(null);

  // Saisie libre : chaque changement de filtre relance le fetch → on temporise la frappe.
  const [referenceInput, setReferenceInput] = React.useState<string>(filters.reference ?? '');
  React.useEffect(() => {
    const value = referenceInput.trim() || null;
    if (value === (filters.reference ?? null)) return;
    const timer = setTimeout(() => dispatch(setFilters({ reference: value })), 400);
    return () => clearTimeout(timer);
  }, [referenceInput, filters.reference, dispatch]);

  const handleChange = (field: string, value: any) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReset = () => {
    dispatch(resetFilter());
    setCurrentTiersType(null);
    setReferenceInput('');
    setResetKey((prev) => prev + 1);
  };

  const handleContratChange = (contrat: any | null) => {
    dispatch(setFilters({ contrat_id: contrat ? contrat.id : null }));
  };

  const handleDepotChange = (depot: GeneriqueData | null) => {
    dispatch(setFilters({ depot_id: depot ? depot.id : null }));
  };

  const handleDepartementChange = (departement: Departement | null) => {
    dispatch(setFilters({ departement_id: departement ? departement.id : null }));
  };

  const handleNumeroDocChange = (document_vente: Document_vente | null) => {
    dispatch(setFilters({ numero_doc: document_vente ? document_vente.numero_doc : null }));
  };

  const handleOnSystypeChange = (systype: SysTypeDocument | null) => {
    const newTiersType = systype?.type_tiers || null;
    setCurrentTiersType(newTiersType);
    dispatch(setFilters({
      type_doc: systype ? systype.id : null,
      tiers_id: null,
      statut_id: null, // reset statut quand le type doc change
    }));
  };

  const handleStatutChange = (statut: WorkflowStatut | null) => {
    dispatch(setFilters({ statut_id: statut ? statut.id : null }));
  };

  const handleTiersChange = (tiers: any | null) => {
    dispatch(setFilters({
      tiers_id: tiers ? tiers.id : null,
    }));
  };

  const renderTiersAutoComplete = () => {
    switch (currentTiersType) {
      case TypeTiers.CLIENT:
        return (
          <ClientAutoC  
            key={`client-${resetKey}`}
            onClientChange={handleTiersChange} 
          />
        );
      case TypeTiers.FOURNISSEUR:
        return (
          <FournisseurAutoC 
            key={`fournisseur-${resetKey}`}
            onFournisseurChange={handleTiersChange}
          />
        );
      default:
        return null;
    }
  };

  // Chaque enfant direct d'AppFilterGrid = une case : les filtres conditionnels
  // (tiers, département) ne laissent aucun trou quand ils sont masqués.
  return (
    <AppFilterGrid>
      {/* type_doc */}
      <SelectSysTypeDocument
        onSysTypeChange={handleOnSystypeChange}
        initialId={filters.type_doc}
        showAll
      />

      {/* contrat_id */}
      <ContratAutoC onContratChange={handleContratChange} />

      {/* numero_doc */}
      <NumeroDocAutoC
        key={`numDoc${resetKey}`}
        onNumeroDocChange={handleNumeroDocChange}
      />

      {/* reference */}
      <TextField
        fullWidth
        label="Référence"
        value={referenceInput}
        onChange={(e) => setReferenceInput(e.target.value)}
        size="small"
      />

      {/* tiers_id — seulement une fois le type de document choisi */}
      {renderTiersAutoComplete()}

      {/* depot_id */}
      <SelectDepot
        key={`depot-${resetKey}`}
        onDepotChange={handleDepotChange}
      />

      {/* departement_id — filtre utile seulement en mode "Tous départements" :
          dans un contexte de département actif, tout est déjà scopé dessus. */}
      {isAllMode && (
        <SelectDepartement
          key={`departement-${resetKey}`}
          onDepartementChange={handleDepartementChange}
          allowAll
        />
      )}

      {/* statut_id */}
      <SelectWorkflowStatut
        key={`statut-${resetKey}`}
        onStatutChange={handleStatutChange}
        selectedStatutId={filters.statut_id ?? null}
      />

      {/* date_debut */}
      <TextField
        fullWidth
        label="Date début"
        type="date"
        value={filters.date_debut ?? ''}
        onChange={(e) => handleChange('date_debut', e.target.value || null)}
        InputLabelProps={{ shrink: true }}
        size="small"
      />

      {/* date_fin */}
      <TextField
        fullWidth
        label="Date fin"
        type="date"
        value={filters.date_fin ?? ''}
        onChange={(e) => handleChange('date_fin', e.target.value || null)}
        InputLabelProps={{ shrink: true }}
        size="small"
      />

      <AppFilterResetButton onClick={handleReset} />
    </AppFilterGrid>
  );
};

export default DocumentVenteFilter;